import {
  AutomationsPresentationV1,
  AutomationsPresentationV1Node
} from 'Automations/automationEditor'
import {
  IntermediateNode,
  getStartNode,
  transformNodeGraphToIntermediateNodeArray
} from 'Automations/nodeDefinitions/helpers'
import {
  NodeType,
  ResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/types'
import type { SemanticType } from 'Automations/nodeDefinitions/semanticTypes'
import {
  ConditionalOperator,
  ConditionalProperties,
  ConditionalProperty
} from '../../conditions.types'

const toSemanticTypeArray = (
  value: SemanticType | SemanticType[] | undefined
): SemanticType[] => {
  if (value === undefined) return []
  return Array.isArray(value) ? value : [value]
}

/**
 * Resolves the effective semantic-type requirement for an operator:
 * its own `valueSemanticType` when defined, otherwise the parent
 * property's. The returned list is used both to gate the operator's
 * visibility (every entry must be in upstream outputs) and to pick the
 * `property` runtime path for the function string.
 */
export const getEffectiveSemanticTypes = (
  property: ConditionalProperty,
  operator: ConditionalOperator
): SemanticType[] => {
  if (operator.valueSemanticType !== undefined) {
    return toSemanticTypeArray(operator.valueSemanticType)
  }
  return toSemanticTypeArray(property.valueSemanticType)
}

export const getUpstreamConditionalProperties = ({
  nodeId,
  presentation,
  definitions
}: {
  nodeId: string
  presentation: AutomationsPresentationV1
  definitions: ResolvedNodeDefinitions
}): ConditionalProperties | undefined => {
  const nodeExistsAnywhere =
    presentation.nodes.some(n => n.id === nodeId) ||
    presentation.edges.some(e => e.source === nodeId || e.target === nodeId)

  if (!nodeExistsAnywhere) {
    return undefined
  }

  const startNode = getStartNode(presentation, definitions)
  if (!startNode) return undefined

  const graph =
    transformNodeGraphToIntermediateNodeArray(definitions)(presentation)
  const upstreamNodes = findUpstreamNodes(graph, nodeId, [])

  if (upstreamNodes === null) return undefined

  // Collect every semantic type any upstream node's outputMapping
  // produces. An operator whose effective `valueSemanticType` lists a
  // type that isn't in this set is hidden — it would have nothing to
  // resolve at runtime. A property is hidden when every one of its
  // operators is hidden.
  const availableSemanticTypes = upstreamNodes.reduce<Set<SemanticType>>(
    (acc, node) => {
      if (!node.type) return acc
      const definition = definitions[node.type as NodeType]
      const outputs = definition?.outputMapping?.(node.id) ?? {}
      Object.values(outputs).forEach(output => acc.add(output.semanticType))
      return acc
    },
    new Set()
  )

  // Read each upstream node's provided properties from the resolved registry
  // (any fetched data — e.g. one operator per customer custom-property key — is
  // already concrete), then merge, applying semantic-type gating.
  const providedPerNode = upstreamNodes.map(node => {
    if (!node.type) return undefined
    const definition = definitions[node.type as NodeType]
    return definition?.providesConditionalProperties
  })

  const conditionalProperties = providedPerNode.reduce<ConditionalProperties>(
    (acc, provided) => {
      if (!provided) return acc
      const filtered = Object.entries(provided).reduce<ConditionalProperties>(
        (inner, [key, property]) => {
          const availableOperators = Object.entries(property.operators).reduce<
            Record<string, ConditionalOperator>
          >((operatorAcc, [operatorKey, operator]) => {
            const required = getEffectiveSemanticTypes(property, operator)
            const allPresent = required.every(t =>
              availableSemanticTypes.has(t)
            )
            return allPresent
              ? { ...operatorAcc, [operatorKey]: operator }
              : operatorAcc
          }, {})
          if (Object.keys(availableOperators).length === 0) return inner
          return {
            ...inner,
            [key]: { ...property, operators: availableOperators }
          }
        },
        {}
      )
      return { ...acc, ...filtered }
    },
    {}
  )

  if (Object.keys(conditionalProperties).length === 0) return undefined

  return conditionalProperties
}

const matchesNodeId = (uniqueBranchId: string, originalId: string): boolean => {
  return (
    uniqueBranchId === originalId ||
    uniqueBranchId.startsWith(`${originalId}__`)
  )
}

const findUpstreamNodes = (
  nodes: IntermediateNode[],
  targetId: string,
  accumulated: AutomationsPresentationV1Node[]
): AutomationsPresentationV1Node[] | null => {
  if (nodes.length === 0) return null

  const [head, ...tail] = nodes

  if (matchesNodeId(head.node.id, targetId)) return accumulated

  const newAccumulated = [...accumulated, head.node]

  for (const branch of head.branches) {
    const result = findUpstreamNodes(branch, targetId, newAccumulated)
    if (result !== null) return result
  }

  return findUpstreamNodes(tail, targetId, newAccumulated)
}
